import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { useTaeFlags } from '@oc/pages/settings/TimesheetSettings/useTaeFlags';
import { SupportWidget } from 'integrations/forethought/SupportWidget';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { FC, Suspense, useCallback, useState } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { MazeSnippet } from 'src/integrations/maze/MazeSnippet';
import { useTrackPageViews } from 'src/integrations/mixpanel/hooks/useTrackPageViews';
import styled from 'styled-components';
import { GraphiQLClient } from 'utils/GraphiQLClient';
import { useTenantCode } from 'utils/hooks/useTenantConfig';
import { WarningBanner } from '../WarningBanner/WarningBanner';
import {
  isChartVisible,
  isContractsVisible,
  isImportVisible,
  isInvoicesVisible,
  isPersonVisible,
  isProgramAlertsVisible,
  isSettingVisible,
  isSourcingVisible,
  isSupplierVisible,
  isTAEVisible,
  isWorkRequestVisible
} from '../common/auth/Authorize';
import { PAGE_ROUTES as Routes } from '../pages/pageRoutes';
import { useFeatureFlags } from '../common/featureFlag/useFeatureFlags';
import { useProjectsFeatureFlags } from '../pages/Projects/permissions/useProjectsFeatureFlags';
import { DocsDrawer } from '../pages/docsSidePanel/DocsDrawer';
import { DailyBriefingDrawer } from '../pages/dailyBriefingSidePanel/DailyBriefingDrawer';
import { useDailyBriefingEnabled } from '../pages/dailyBriefingSidePanel/useDailyBriefingEnabled';
import { MainRoute } from './MainRoute';
import Header from './header/Header';
import { Menu } from './menu/Menu';
import { useScreenSizes } from './useScreenSizes';
import { bpeConfig } from '@enterprise/common';
import { AuthUser } from 'src/providers/User/AuthUserModels';
import { useAppVersionHasChanged } from 'utils/hooks/useAppVersionHasChanged';
import { Loading } from '@beeline.com/bp-design-system';

interface AppProps {
  sessionUser: AuthUser;
}

function isDevToolsEnabled() {
  return bpeConfig.isEnvironmentType('dev');
}

const App: FC<React.PropsWithChildren<AppProps>> = ({ sessionUser }) => {
  const [isHelpOpen, setHelpOpen] = useState(false);
  const [isBriefingOpen, setBriefingOpen] = useState(false);
  const { isEnabled, isEnabledSubFeature } = useFeatureFlags();
  const tenantCode = useTenantCode();
  const { supportWidget = false, mazeSnippet = false } = useDarkly();
  const isDailyBriefingEnabled = useDailyBriefingEnabled();
  const { isEnabled: isProjectFlagEnabled } = useProjectsFeatureFlags();
  const { isAnyMobileScreen, isTabletScreen } = useScreenSizes();
  const { invoices } = useTaeFlags();
  const isSourcingEnabled = isEnabled('sourcing') && isSourcingVisible(sessionUser);
  const isProgramAlertsEnabled = isEnabled('program-alerts-detection');
  const timesheetAndExpensesHub = isEnabled('time-and-expenses-hub') && isEnabled('time-and-expenses');
  const isInvoicingEnabled = invoices.enabled && isInvoicesVisible(sessionUser);
  const isInvoicingV2Enabled = invoices.v2 && isInvoicesVisible(sessionUser);
  const isContractsHubEnabled = isEnabledSubFeature('sourcing', 'contract') && isContractsVisible(sessionUser);
  const isWorkRequestEnabled = isWorkRequestVisible(sessionUser);

  useAppVersionHasChanged();
  useTrackPageViews();

  const onHelpClick = useCallback(() => {
    setHelpOpen((prevState) => !prevState);
  }, []);
  const onBriefingClick = useCallback(() => {
    setBriefingOpen((prevState) => !prevState);
  }, []);
  return (
    <StyledApp>
      <WarningBanner />
      <SupportWidget enabled={supportWidget} />
      <MazeSnippet enabled={mazeSnippet} />
      <Header user={sessionUser} onHelpClick={onHelpClick} isHelpOpen={isHelpOpen} onBriefingClick={onBriefingClick} />
      <StyledAppContent data-test="app-content">
        {!isAnyMobileScreen && !isTabletScreen && <Menu user={sessionUser} />}
        <StyledAppBody>
          <Suspense
            fallback={
              <>
                <div />
                <Loading className="main_loader" size="large" />
              </>
            }
          >
            <Switch>
              {/* MAIN PAGE */}
              <MainRoute path="/:tenantCode/myutmost" component={Routes.MyHomePage} />

              {/* FRONTDOOR */}
              <MainRoute
                path="/:tenantCode/frontdoor"
                component={Routes.Frontdoor}
                enabled={isEnabled('smart-forms')}
              />

              {/* IMPORTS */}
              <MainRoute
                path="/:tenantCode/imports"
                component={Routes.QuickStart}
                enabled={isImportVisible(sessionUser)}
              />

              {/* DATA EXTRACTS */}
              <MainRoute path="/:tenantCode/data-extracts" component={Routes.DataExtracts} />

              {/* REPORTS */}
              <MainRoute path="/:tenantCode/reports" exact={false} component={Routes.Reports} />

              {/* DASHBOARDS */}
              <MainRoute path="/:tenantCode/dashboards" component={Routes.Dashboards} />
              <MainRoute path="/:tenantCode/analytics-dashboards" component={Routes.AnalyticsDashboards} />
              <MainRoute
                path="/:tenantCode/chart"
                component={Routes.OrgNavigatorPageContainer}
                enabled={isChartVisible(sessionUser)}
              />

              {/* ENGAGEMENT HUB */}
              <MainRoute
                path="/:tenantCode/engagements"
                component={Routes.EngagementsPage}
                enabled={isEnabled('engagement-hub')}
              />

              {/* PROGRAM ALERTS HUB */}
              {isProgramAlertsEnabled && (
                <MainRoute
                  path="/:tenantCode/program-alerts"
                  component={Routes.ProgramAlerts}
                  enabled={isProgramAlertsVisible(sessionUser)}
                  exact={false}
                />
              )}

              {/* WORKER HUB */}
              <MainRoute path="/:tenantCode/workers" component={Routes.WorkersPage} />

              {/* WORKER PROFILE */}
              <MainRoute
                path="/:tenantCode/profile/:user"
                component={Routes.WorkerProfilePage}
                enabled={isPersonVisible(sessionUser)}
              />

              {/* SUPPLIER HUB */}
              <MainRoute path="/:tenantCode/suppliers" component={Routes.NewSuppliersPage} />
              <MainRoute
                path="/:tenantCode/supplier/:supplierUid"
                component={Routes.SupplierPage}
                enabled={isSupplierVisible(sessionUser)}
              />

              {/* WORK REQUESTS */}
              <MainRoute
                path="/:tenantCode/work-requests"
                exact
                component={Routes.WorkRequestsPage}
                enabled={isWorkRequestEnabled}
              />
              <MainRoute
                path="/:tenantCode/work-requests/:workRequestUid"
                component={Routes.WorkRequestPage}
                enabled={isWorkRequestEnabled}
              />

              {/* JOB POSTINGS */}
              <MainRoute
                path="/:tenantCode/job-postings"
                exact
                component={Routes.JobRequestsPage}
                enabled={isSourcingEnabled}
              />
              <MainRoute
                path="/:tenantCode/job-postings/:jobRequestUid"
                component={Routes.JobRequestPage}
                enabled={isSourcingEnabled}
              />

              {/* JOB APPLICATIONS */}
              <MainRoute
                path="/:tenantCode/job-applications"
                exact
                component={Routes.JobApplicationsPage}
                enabled={isSourcingEnabled}
              />
              <MainRoute
                path="/:tenantCode/job-applications/:jobApplicationUid"
                component={Routes.JobApplicationPage}
                enabled={isSourcingEnabled}
              />

              {/* TIME AND EXPENSES */}
              <MainRoute
                path="/:tenantCode/time-and-expenses"
                component={Routes.TimeAndExpenses}
                enabled={timesheetAndExpensesHub && isTAEVisible(sessionUser)}
              />

              {/* INVOICES */}
              <MainRoute
                path="/:tenantCode/invoices"
                component={isInvoicingV2Enabled ? Routes.InvoicesV2 : Routes.Invoices}
                enabled={isInvoicingEnabled || isInvoicingV2Enabled}
              />

              {/* SETTINGS */}
              <MainRoute
                path="/:tenantCode/settings"
                exact={false}
                component={Routes.Settings}
                enabled={isSettingVisible(sessionUser)}
              />

              {/* GRAPHIQL */}
              <MainRoute
                path="/:tenantCode/graphiql"
                component={() => <GraphiQLClient />}
                enabled={isDevToolsEnabled()}
              />

              {/* MY ACCOUNT */}
              <MainRoute path="/:tenantCode/my-account" exact={false} component={Routes.MyAccount} />

              {/* TASKS */}
              <MainRoute path="/:tenantCode/tasks" component={Routes.TasksPage} enabled={isEnabled('routes-tasks')} />

              {/* ALL TASKS */}
              <MainRoute
                path="/:tenantCode/all-tasks"
                component={Routes.TasksPage}
                enabled={isEnabled('routes-tasks')}
              />

              {/* CONTRACTS */}
              <MainRoute
                path="/:tenantCode/contracts"
                exact
                component={Routes.ContractsHub}
                enabled={isContractsHubEnabled}
              />
              <MainRoute
                path="/:tenantCode/contracts/:contractUid"
                component={Routes.ContractDetails}
                enabled={isContractsHubEnabled}
              />

              {/* PROJECT */}
              <MainRoute
                path="/:tenantCode/projects/:projectUid?"
                component={Routes.ProjectsPage}
                enabled={isProjectFlagEnabled}
              />

              {/* DOCUMENTATION */}
              <MainRoute path="/documentation" component={Routes.ApiDocumentation} />

              <Redirect key="tenantCodeRoot" from="/:tenantCode" to="/:tenantCode/myutmost" />
              <Redirect key="root" from="/" to={`/${tenantCode}/myutmost`} />
            </Switch>
          </Suspense>
        </StyledAppBody>
        {<DocsDrawer isOpen={isHelpOpen} onClose={onHelpClick} />}
        {isDailyBriefingEnabled && <DailyBriefingDrawer isOpen={isBriefingOpen} onClose={onBriefingClick} />}
      </StyledAppContent>
    </StyledApp>
  );
};

export default App;

const StyledApp = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms; /*TODO transitions don't work on grid yet argh!!*/
`;

const StyledAppContent = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
`;

const StyledAppBody = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: ${({ theme }) => theme.colorBackground};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;
